"use client";

import React, { useState } from "react";
import { Download, FileText, CircleCheck } from "lucide-react";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "@/components/common/Link";
const technicalData = [{
  id: 1,
  feature: "Service Area",
  value: "Broward County, FL"
}, {
  id: 2,
  feature: "Inspection Type",
  value: "Full Residential"
}, {
  id: 3,
  feature: "Report Delivery",
  value: "Within 24 Hours"
}, {
  id: 4,
  feature: "Certifications",
  value: "InterNACHI Certified"
}, {
  id: 5,
  feature: "Experience",
  value: "15+ Years"
}, {
  id: 6,
  feature: "Insurance",
  value: "General Liability & E&O"
}, {
  id: 7,
  feature: "Availability",
  value: "Mon - Sat, 8am - 6pm"
}];
function ProductDetailConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
        <div className="space-y-4">
          <div className="aspect-square bg-muted rounded-lg w-full"></div>
          <div className="flex gap-4">
            {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="aspect-square bg-muted rounded-md w-1/4"></div>)}
          </div>
        </div>
        <div className="space-y-6">
          <div className="h-12 bg-muted rounded w-3/4"></div>
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
          </div>
          <div className="h-64 bg-muted rounded w-full mt-8"></div>
        </div>
      </div>;
  }
  if (error || products.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground font-sans">Inspection service details could not be loaded at this time.</p>
      </div>;
  }
  const product = products[0];
  const galleryImages = [product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/broward_home_inspection_main.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/broward_roof_check.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/broward_electrical_panel.png", "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/broward_hvac_inspection.png"];
  return <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
      {}
      <div className="space-y-6 sticky top-24">
        <div className="aspect-square bg-card rounded-xl overflow-hidden border border-border shadow-lg flex items-center justify-center p-8">
          <img src={galleryImages[activeImageIndex]} alt={product.title} className="w-full h-full object-cover" />
        </div>
        
        <Carousel opts={{
        align: "start"
      }} className="w-full">
          <CarouselContent className="-ml-4">
            {galleryImages.map((img, i) => <CarouselItem key={i} data-index={i} className="pl-4 basis-1/3 sm:basis-1/4">
                <Button onClick={() => setActiveImageIndex(i)} variant="outline" className={`aspect-square w-full p-2 transition-all rounded-lg ${activeImageIndex === i ? "border-primary border-2" : "border-border hover:border-primary/50"}`} aria-label={`View image ${i + 1}`}>
                  <img src={img} alt={`${product.title} thumbnail ${i + 1}`} className="w-full h-full object-cover rounded-sm" />
                </Button>
              </CarouselItem>)}
          </CarouselContent>
          <CarouselPrevious className="left-2 bg-background/90 backdrop-blur-md" />
          <CarouselNext className="right-2 bg-background/90 backdrop-blur-md" />
        </Carousel>
      </div>

      {}
      <div className="flex flex-col space-y-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary uppercase tracking-wider">
              Available Now
            </span>
            <span className="text-sm text-muted-foreground font-mono">
              ID: {product.customFields?.sku || "BRW-2024-INS"}
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold font-default text-foreground mb-4 leading-tight tracking-tighter">
            Home Inspector in Broward County, FL
          </h1>
          
          <div className="text-lg text-muted-foreground font-sans prose prose-slate max-w-none [&>p]:mb-4">
            <p>Comprehensive property evaluations for homebuyers and sellers across Broward County. We provide detailed, easy-to-read digital reports with high-resolution photos and clear explanations of all findings.</p>
          </div>
          
          <ul className="mt-6 space-y-3">
            <li className="flex items-start gap-3 text-base text-foreground font-sans">
              <CircleCheck className="w-6 h-6 text-primary shrink-0" />
              <span>Thorough structural, electrical, plumbing, and HVAC assessments.</span>
            </li>
            <li className="flex items-start gap-3 text-base text-foreground font-sans">
              <CircleCheck className="w-6 h-6 text-primary shrink-0" />
              <span>Same-day digital reports with interactive photo galleries.</span>
            </li>
            <li className="flex items-start gap-3 text-base text-foreground font-sans">
              <CircleCheck className="w-6 h-6 text-primary shrink-0" />
              <span>Free thermal imaging included with every full home inspection.</span>
            </li>
          </ul>
        </div>

        {}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
          <div className="flex-1">
            <AddToCartButton productId={product.id} text="Schedule Inspection" showQuantity={false} className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-bold rounded-lg shadow-md transition-transform active:scale-95" />
          </div>
          <Button variant="outline" className="h-14 px-8 font-bold border-2 rounded-lg" asChild>
            <Link to="https://trusted-fl.com/#our-inspection-services">
              <Download className="w-5 h-4 mr-2" />
              View Inspection Services
            </Link>
          </Button>
        </div>

        {}
        <div className="pt-8">
          <h2 className="text-2xl font-bold font-default text-foreground mb-4 flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Service Specifications
          </h2>
          <div className="border border-border rounded-xl overflow-hidden bg-card shadow-sm">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="w-1/2 font-bold text-foreground py-4">Category</TableHead>
                  <TableHead className="w-1/2 font-bold text-foreground py-4">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {technicalData.map((item, i) => <TableRow key={item.id} data-index={i} className="hover:bg-muted/10 transition-colors">
                    <TableCell className="font-semibold text-muted-foreground py-4">{item.feature}</TableCell>
                    <TableCell className="text-foreground font-medium py-4">{item.value}</TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>;
}
export default function CableTraysAndConnectors() {
  const wp_query_params = {
    post_type: "product",
    per_page: 1
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="239046" id="hero" className="py-24 bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <EcommerceProductsProvider wp_query={{
        post_type: "product",
        per_page: 1
      }}>
          <ProductDetailConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}